import { useState, useEffect, useMemo, Fragment } from "react";
import {
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  getActiveUsersMetrics,
  type TActiveUsersResponse,
  type TActiveUsersSupplier,
} from "@/api/customs/consumo";

export const PivotTableActiveUsers = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TActiveUsersResponse | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [expandedSuppliers, setExpandedSuppliers] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getActiveUsersMetrics();
        setData(response);
        const expanded: Record<string, boolean> = {};
        for (const sid of Object.keys(response.suppliers)) {
          expanded[sid] = true;
        }
        setExpandedSuppliers(expanded);
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            "Error al cargar los datos de Active Users"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const toggleSupplier = (supplierId: string) => {
    setExpandedSuppliers((prev) => ({
      ...prev,
      [supplierId]: !prev[supplierId],
    }));
  };

  const { supplierEntries, filterOptions, displayMonths, thresholds } =
    useMemo(() => {
      if (!data || Object.keys(data.suppliers).length === 0) {
        return {
          supplierEntries: [] as [string, TActiveUsersSupplier][],
          allMonths: [] as string[],
          filterOptions: [] as string[],
          displayMonths: [] as string[],
          thresholds: {} as Record<string, number>,
        };
      }

      // Only keep months from current year (2026)
      const currentYear = new Date().getFullYear().toString();
      const allMonths = data.months.filter((m) => m.startsWith(currentYear));
      const thresholds = data.thresholds;

      const supplierEntries = Object.entries(data.suppliers).sort(
        ([, a], [, b]) =>
          a.supplier_name
            .toLowerCase()
            .localeCompare(b.supplier_name.toLowerCase())
      );

      // Filter options sorted descending (newest first)
      const filterOptions = [...allMonths].sort((a, b) => b.localeCompare(a));

      // Display months sorted ascending (left to right)
      const displayMonths = selectedMonth ? [selectedMonth] : allMonths;

      return { supplierEntries, allMonths, filterOptions, displayMonths, thresholds };
    }, [data, selectedMonth]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data || Object.keys(data.suppliers).length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No hay datos disponibles.
      </Alert>
    );
  }

  // --- helpers ---
  const getSupplierMonthActiveUsers = (supplier: TActiveUsersSupplier, m: string) =>
    supplier.months[m]?.active_users ?? 0;

  // --- colors (dark-theme friendly) ---
  const activeColor = "rgba(76, 175, 80, 0.25)"; // green tint
  const inactiveColor = "rgba(244, 67, 54, 0.25)"; // red tint
  const supplierBg = "rgba(255, 255, 255, 0.08)";
  const headerBg = "rgb(30, 30, 30)";

  // --- styles (theme-aware, no hardcoded light colors) ---
  const baseCellSx = {
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
    px: 1.5,
    py: 0.75,
    fontSize: "0.85rem",
    borderBottom: "1px solid rgba(255,255,255,0.12)",
  };

  const headerCellSx = {
    ...baseCellSx,
    fontWeight: 700,
    backgroundColor: headerBg,
  };

  const supplierNameCellSx = {
    ...baseCellSx,
    textAlign: "left" as const,
    fontWeight: 700,
    backgroundColor: supplierBg,
    position: "sticky" as const,
    left: 0,
    zIndex: 1,
    minWidth: 220,
    cursor: "pointer",
    userSelect: "none" as const,
  };

  const supplierDataCellSx = {
    ...baseCellSx,
    fontWeight: 700,
    backgroundColor: supplierBg,
  };

  const userNameCellSx = {
    ...baseCellSx,
    textAlign: "left" as const,
    pl: 5,
    fontWeight: 400,
    position: "sticky" as const,
    left: 0,
    zIndex: 1,
    minWidth: 220,
    backgroundColor: "inherit",
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Autocomplete
            options={filterOptions}
            value={selectedMonth}
            onChange={(_, v) => setSelectedMonth(v)}
            renderInput={(params) => (
              <TextField {...params} label="Filtrar por mes" size="small" />
            )}
            sx={{ maxWidth: 260 }}
            clearText="Mostrar todos"
            noOptionsText="Sin opciones"
          />
        </CardContent>
      </Card>

      <TableContainer
        component={Paper}
        sx={{ maxHeight: "70vh", overflow: "auto" }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headerCellSx,
                  textAlign: "left",
                  position: "sticky",
                  left: 0,
                  zIndex: 3,
                  minWidth: 220,
                }}
              >
                Proveedor / Usuario
              </TableCell>
              {displayMonths.map((m) => (
                <TableCell key={m} sx={headerCellSx}>
                  {m}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {supplierEntries.map(([supplierId, supplier]) => {
              const isExpanded = !!expandedSuppliers[supplierId];

              return (
                <Fragment key={supplierId}>
                  {/* Supplier row */}
                  <TableRow>
                    <TableCell
                      sx={supplierNameCellSx}
                      onClick={() => toggleSupplier(supplierId)}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <IconButton size="small" sx={{ p: 0, color: "inherit" }}>
                          {isExpanded ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                          )}
                        </IconButton>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {supplier.supplier_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    {displayMonths.map((m) => {
                      const active = getSupplierMonthActiveUsers(supplier, m);
                      return (
                        <TableCell key={m} sx={supplierDataCellSx}>
                          {supplier.months[m] ? `Activos: ${active}` : ""}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {/* User rows */}
                  {isExpanded &&
                    supplier.users.map((user, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={userNameCellSx}>{user.name}</TableCell>
                        {displayMonths.map((m) => {
                          const days = user.months[m] ?? 0;
                          const threshold = thresholds[m] ?? 0;
                          const isActive = days >= threshold;
                          const bg =
                            days > 0
                              ? isActive
                                ? activeColor
                                : inactiveColor
                              : undefined;
                          return (
                            <TableCell
                              key={m}
                              sx={{
                                ...baseCellSx,
                                backgroundColor: bg,
                              }}
                            >
                              {days > 0 ? days : ""}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
